import { useEffect, useState } from 'react';
import { Store } from 'redux';
import { appnebulaConfig } from 'src/config';
import { AnalyticsPixelIds } from 'core/analytics/entities';
import { initDependencies } from 'core/common/contexts';
import { useIsomorphicLayoutEffect, useUrlLocation } from 'core/common/hooks';
import { RootInitialState } from 'core/common/store';
import createStore from 'core/common/store/store';
import { getThemeType } from 'core/theme_deprecated/utils/getFlowThemeType';

type UseAppParams = {
  analyticsPixelIds: AnalyticsPixelIds;
};

export const useApp = ({ analyticsPixelIds }: UseAppParams) => {
  const { location: urlLocation } = useUrlLocation();

  const theme = getThemeType(urlLocation);
  const [appDependencies] = useState(() =>
    initDependencies(appnebulaConfig, { analyticsPixelIds }),
  );

  const { featureFlags, services } = appDependencies;
  const { cookieConsentService } = services;

  const [appStore] = useState<Store<RootInitialState>>(() => createStore(services, featureFlags));

  useEffect(() => {
    cookieConsentService.initialize();
  }, [cookieConsentService]);

  useIsomorphicLayoutEffect(() => {
    services.analyticsService.launch();
    services.loggingService.info('App services initialized');
    // eslint-disable-next-line no-console
    console.log('✅ Version:', appnebulaConfig.appVersion);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const deviceId = services.analyticsService.getDeviceId();
    services.errorMonitoringService.identify(deviceId);
  }, [services.analyticsService, services.errorMonitoringService]);

  useEffect(() => {
    featureFlags.onAttributesUpdated((attributes) => {
      services.analyticsService.setFeatureFlagsAttributes(attributes);

      if (attributes.id) {
        services.analyticsService.addUserAssociatedId(attributes.id);
      }
    });

    services.heatmapRecordingService.initialize();
  }, [services, featureFlags]);

  useEffect(() => {
    window.scroll(0, 0);
  }, [urlLocation.pathname]);

  return {
    config: appnebulaConfig,
    appStore,
    theme,
    featureFlags,
    services,
  };
};
